import { Link, routes } from '@redwoodjs/router'
import { MetaTags } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import IconAlertTriangle from 'src/icons/IconAlertTriangle'
import IconEmployment from 'src/icons/IconEmployment'
import IconPuzzle from 'src/icons/IconPuzzle'
import IconShield from 'src/icons/IconShield'

const TOOLS = [
  {
    name: 'Employment Verifications',
    description:
      'Employer contacts used for verification outreach. Look one up, correct it everywhere at once, or block one we have been asked never to use again.',
    icon: IconEmployment,
    route: () => routes.adminVoeCarriers(),
  },
  {
    name: 'Integration',
    description:
      'Which partner API clients touch which companies, and with which scopes. Review what the API has seen, add a row, or remove one that should not be there.',
    icon: IconPuzzle,
    route: () => routes.adminIntegrationApiClients(),
  },
]

const AdminHomePage = () => {
  const { currentUser } = useAuth()

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-8 px-6 pb-16">
      <MetaTags
        title="Admin"
        description="Double Nickel internal admin tools"
      />

      <div className="flex flex-col gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-doubleNickel-brand-500">
          <IconShield className="h-6 w-6 fill-none stroke-white" />
        </div>
        <h1 className="text-2xl font-semibold text-doubleNickel-gray-900">
          Double Nickel Admin
        </h1>
        <p className="text-sm leading-relaxed text-doubleNickel-gray-600">
          Internal tools for data that Double Nickel owns rather than any single
          client. Everything here sits outside the company boundary the
          dashboard is built around.
        </p>
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-doubleNickel-warning-200 bg-doubleNickel-warning-25 p-4">
        <div className="flex flex-row items-center gap-2">
          <IconAlertTriangle className="h-4 w-4 fill-none stroke-doubleNickel-warning-600" />
          <span className="text-sm font-semibold text-doubleNickel-gray-900">
            Changes here are not company-scoped
          </span>
        </div>
        <ul className="flex flex-col gap-1.5 pl-6 text-sm leading-relaxed text-doubleNickel-gray-700">
          <li className="list-disc">
            The records these tools edit — carriers, employer contacts — are a
            single directory shared by every client on the platform.
          </li>
          <li className="list-disc">
            An edit made here changes what every other company sees. There is no
            per-company copy to fall back on.
          </li>
          <li className="list-disc">
            Access requires the ADMIN role, and every change is written to an
            audit trail with your user against it.
          </li>
        </ul>
      </div>

      <div className="flex flex-col gap-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-doubleNickel-gray-500">
          Tools
        </div>
        <div className="flex flex-col gap-2">
          {TOOLS.map((tool) => {
            const Icon = tool.icon
            return (
              <Link
                key={tool.name}
                to={tool.route()}
                className="flex flex-row items-start gap-3 rounded-lg border border-doubleNickel-gray-200 bg-white p-4 transition-colors hover:border-doubleNickel-brand-300"
              >
                <div className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-doubleNickel-gray-100">
                  <Icon className="h-4 w-4 fill-none stroke-doubleNickel-gray-600" />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-doubleNickel-gray-900">
                    {tool.name}
                  </span>
                  <span className="text-sm leading-relaxed text-doubleNickel-gray-600">
                    {tool.description}
                  </span>
                </div>
              </Link>
            )
          })}
        </div>
      </div>

      <div className="flex flex-col gap-1 border-t border-doubleNickel-gray-200 pt-5 text-sm text-doubleNickel-gray-600">
        <span>
          Signed in as{' '}
          <span className="font-medium text-doubleNickel-gray-900">
            {currentUser?.email ?? 'an admin'}
          </span>
        </span>
        <span>
          Looking for applicants, jobs or settings?{' '}
          <a
            href="/"
            className="font-medium text-doubleNickel-brand-500 underline"
          >
            Go to the dashboard
          </a>
          .
        </span>
      </div>
    </div>
  )
}

export default AdminHomePage
